import { useCallback, useEffect, useState } from 'react';
import {
  getTaxSummaries,
  getTaxSummaryPreviewUrl,
  updateTaxSummaryStatus,
} from '../services/taxFilingsService';
import type { TaxFilingDocument } from '../types/taxFilings.types';
import { DRAFT_COPY_STATUS } from '../types/taxFilings.types';
import { useTaxYearStore } from '@/features/tax-year/store/taxYearStore';
import { handleApiError } from '@/lib/utils/errorHandler';
import { toast } from 'sonner';

export function useTaxSummaries() {
  const [documents, setDocuments] = useState<TaxFilingDocument[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const effectiveYear = useTaxYearStore((s) => s.getEffectiveYear());
  const configFetched = useTaxYearStore((s) => s.configFetched);

  const fetchDocuments = useCallback(async () => {
    if (effectiveYear == null) return;
    setIsLoading(true);
    setError(null);
    try {
      const list = await getTaxSummaries(effectiveYear);
      setDocuments(list);
    } catch (err) {
      const apiError = handleApiError(err);
      setError(apiError.message);
      setDocuments([]);
    } finally {
      setIsLoading(false);
    }
  }, [effectiveYear]);

  useEffect(() => {
    if (!configFetched || effectiveYear == null) return;
    fetchDocuments();
  }, [configFetched, effectiveYear, fetchDocuments]);

  const getPreviewUrl = useCallback(async (documentId: string): Promise<string | null> => {
    try {
      return await getTaxSummaryPreviewUrl(documentId);
    } catch (err) {
      handleApiError(err);
      toast.error('Could not open preview');
      return null;
    }
  }, []);

  const approve = useCallback(
    async (documentId: string) => {
      try {
        await updateTaxSummaryStatus(documentId, DRAFT_COPY_STATUS.CLIENT_VERIFIED);
        toast.success('Tax summary verified');
        await fetchDocuments();
      } catch (err) {
        handleApiError(err);
        toast.error('Failed to verify tax summary');
        throw err;
      }
    },
    [fetchDocuments]
  );

  const reject = useCallback(
    async (documentId: string, rejectionComment?: string) => {
      try {
        await updateTaxSummaryStatus(
          documentId,
          DRAFT_COPY_STATUS.CLIENT_REJECTED,
          rejectionComment
        );
        toast.success('Tax summary rejected');
        await fetchDocuments();
      } catch (err) {
        handleApiError(err);
        toast.error('Failed to reject tax summary');
        throw err;
      }
    },
    [fetchDocuments]
  );

  return {
    documents,
    isLoading: isLoading || !configFetched,
    error,
    refresh: fetchDocuments,
    getPreviewUrl,
    approve,
    reject,
  };
}
